import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { user, login, register, logout } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [heroSettings, setHeroSettings] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
    fetchHeroSettings();
  }, [user]);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchHeroSettings = async () => {
    try {
      const response = await axios.get(`${API}/hero/settings`);
      setHeroSettings(response.data);
    } catch (error) {
      console.error('Error fetching hero settings:', error);
      // Use default if error
      setHeroSettings({
        media_type: 'image',
        media_url: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920&q=80',
        title: 'Le Registre',
        subtitle: 'Journal d\'observation · Elara Voss',
        description: 'Je consigne ici mes mesures, mes méthodes et ce qui résiste encore à l\'explication. Les données ne mentent pas. Ce journal non plus.',
        badge_text: 'Système d\'observation personnel · accès public partiel'
      });
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      if (authMode === 'login') {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.password);
      }
      setShowAuthModal(false);
      setFormData({ name: '', email: '', password: '' });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-content">
          <a href="/" className="navbar-logo" data-testid="navbar-logo">LE REGISTRE</a>
          <div className="navbar-menu">
            {user ? (
              <>
                {user.role === 'admin' && (
                  <a href="/admin" className="navbar-link" data-testid="admin-link">Console</a>
                )}
                <a href="/subscription" className="navbar-link" data-testid="subscription-link">Accès</a>
                <div className="navbar-user" data-testid="user-menu">
                  {user.picture && <img src={user.picture} alt={user.name} className="user-avatar" data-testid="user-avatar" />}
                  <span className="user-name" data-testid="user-name">{user.name}</span>
                  {user.subscription_status === 'premium' && (
                    <span className="premium-badge" data-testid="premium-badge">Accès étendu</span>
                  )}
                  <button onClick={logout} className="btn btn-outline" data-testid="logout-button">Déconnexion</button>
                </div>
              </>
            ) : (
              <button onClick={() => setShowAuthModal(true)} className="btn btn-primary" data-testid="login-button">Se connecter</button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section with Background */}
      <section className="hero-main" data-testid="hero-section">
        {/* Background Image/Video Container */}
        <div className="hero-background">
          {heroSettings?.media_type === 'video' ? (
            <video autoPlay muted loop playsInline className="hero-video" data-testid="hero-video">
              <source src={heroSettings.media_url} type="video/mp4" />
            </video>
          ) : (
            <img 
              src={heroSettings?.media_url || 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920&q=80'} 
              alt="Fenêtre d'observation"
              className="hero-image"
              data-testid="hero-image"
            />
          )}
          
          {/* Dark overlay for better text readability */}
          <div className="hero-overlay"></div>
        </div>

        {/* Hero Content */}
        <div className="hero-content-main" data-testid="hero-content">
          <div className="hero-badge" data-testid="hero-badge">
            {heroSettings?.badge_text || 'Système d\'observation personnel · accès public partiel'}
          </div>
          <h1 className="hero-title-main" data-testid="hero-main-title">
            {heroSettings?.title || 'Le Registre'}
            <span className="hero-title-gradient">{heroSettings?.subtitle || 'Journal d\'observation · Elara Voss'}</span>
          </h1>
          <p className="hero-description" data-testid="hero-description">
            {heroSettings?.description || 'Je consigne ici mes mesures, mes méthodes et ce qui résiste encore à l\'explication. Les données ne mentent pas. Ce journal non plus.'}
          </p>
          <div className="hero-actions" data-testid="hero-actions">
            <button
              onClick={() => navigate('/premiere-recherche')}
              className="btn btn-primary btn-large"
              data-testid="first-entry-button"
            >
              Commencer ici — Entrée 001
            </button>
            <button
              onClick={() => document.getElementById('content-feed')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn btn-secondary btn-large"
              data-testid="explore-button"
            >
              Consulter le journal
            </button>
            {!user && (
              <button
                onClick={() => setShowAuthModal(true)}
                className="btn btn-secondary btn-large"
                data-testid="join-button"
              >
                Créer un accès observateur
              </button>
            )}
            {user && user.subscription_status !== 'premium' && (
              <button
                onClick={() => navigate('/subscription')}
                className="btn btn-secondary btn-large"
                data-testid="premium-button"
              >
                Étendre l'accès
              </button>
            )}
          </div>
          
          {/* Stats */}
          <div className="hero-stats" data-testid="hero-stats">
            <div className="hero-stat-item">
              <span className="hero-stat-number">{String(posts.length).padStart(3, '0')}</span>
              <span className="hero-stat-label">Entrées consignées</span>
            </div>
            <div className="hero-stat-item">
              <span className="hero-stat-number">{String(posts.filter(p => p.is_premium).length).padStart(3, '0')}</span>
              <span className="hero-stat-label">Archives restreintes</span>
            </div>
            <div className="hero-stat-item">
              <span className="hero-stat-number">24/7</span>
              <span className="hero-stat-label">Acquisition</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hero-scroll-indicator" data-testid="scroll-indicator">
          <span>↓</span>
        </div>
      </section>

      {/* Content Feed */}
      <div className="content-container" id="content-feed">
        <div className="content-header">
          <h2 className="content-section-title">Journal d'observation</h2>
          <p className="content-section-subtitle">Entrées · ordre antichronologique</p>
        </div>
        
        <div className="content-grid">
          {posts.map((post) => (
            <div 
              key={post.id} 
              className="post-card" 
              data-testid={`post-card-${post.id}`}
              onClick={() => {
                // Only navigate if not premium or user is premium
                if (!post.is_premium || (user && user.subscription_status === 'premium')) {
                  navigate(`/post/${post.id}`);
                }
              }}
              style={{ cursor: (!post.is_premium || (user && user.subscription_status === 'premium')) ? 'pointer' : 'default' }}
            >
              {post.media_url && (
                <img
                  src={post.media_url}
                  alt={post.title}
                  className={`post-media ${post.is_premium && (!user || user.subscription_status !== 'premium') ? 'locked' : ''}`}
                  data-testid={`post-media-${post.id}`}
                />
              )}
              <div className="post-content">
                <h3 className="post-title" data-testid={`post-title-${post.id}`}>{post.title}</h3>
                <p className="post-text" data-testid={`post-text-${post.id}`}>{post.content}</p>
                <span className="post-date" data-testid={`post-date-${post.id}`}>{formatDate(post.created_at)}</span>
              </div>
              {post.is_premium && (!user || user.subscription_status !== 'premium') && (
                <div className="premium-overlay" data-testid={`premium-overlay-${post.id}`}>
                  <div className="lock-icon">▪ ▪ ▪</div>
                  <span className="premium-text">Accès restreint</span>
                  <button onClick={() => navigate('/subscription')} className="btn btn-primary" data-testid={`unlock-button-${post.id}`}>
                    Étendre l'accès
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAuthModal(false)} data-testid="auth-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title" data-testid="modal-title">{authMode === 'login' ? 'Identification' : 'Accès observateur'}</h2>
            <form onSubmit={handleAuth}>
              {authMode === 'register' && (
                <div className="form-group">
                  <label className="form-label">Nom</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mot de passe</label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowAuthModal(false)} className="btn btn-outline" data-testid="cancel-button">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" data-testid="submit-button">
                  {authMode === 'login' ? 'Se connecter' : "S'inscrire"}
                </button>
              </div>
            </form>
            <div className="google-auth-divider">OU</div>
            <button onClick={handleGoogleLogin} className="btn-google" data-testid="google-login-button">
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continuer avec Google
            </button>
            <div className="form-switch">
              {authMode === 'login' ? (
                <span>
                  Pas encore d'accès ?{' '}
                  <button onClick={() => setAuthMode('register')} data-testid="switch-to-register">Créer un accès</button>
                </span>
              ) : (
                <span>
                  Déjà un accès ?{' '}
                  <button onClick={() => setAuthMode('login')} data-testid="switch-to-login">Se connecter</button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
